/**
 * Mapa coroplético de votação por bairro (proto:745-747).
 *
 * A paleta final do protótipo (proto:1901) substitui os violetas por
 * laranja/dourado Eleg — 4 faixas fixas, usadas literalmente aqui:
 *   cor(p) = p>12 ? '#F58406' : p>5 ? '#FFAE3D' : p>2 ? '#FFD694' : '#FFF5E6'
 * Esses hexadecimais (e os `#fff`/`#3a2f5c` do texto sobre o mapa, também
 * literais no protótipo) são a exceção documentada à regra de "cor via
 * token": fazem parte da paleta do mapa, que vem do protótipo, não do
 * design system.
 *
 * Honestidade de dado: `percentual` e `votos` são o desempenho eleitoral do
 * mandato por bairro (`terr.bairro.votos`/`percentual`), um valor único —
 * não existe uma coluna de ciclo. Por isso este mapa não tem seletor de
 * ciclo (2016/2020/2024, como no protótipo): mostra o retrato mais recente.
 */
export interface BairroVoto {
  id: string;
  nome: string;
  votos: number;
  percentual: number;
  path: string;
  lx: number;
  ly: number;
}

// Escala única: a mesma função decide o preenchimento de cada bairro e a
// cor de cada retângulo da legenda — nenhum dos dois calcula por conta própria.
function cor(p: number): string {
  if (p > 12) return '#F58406';
  if (p > 5) return '#FFAE3D';
  if (p > 2) return '#FFD694';
  return '#FFF5E6';
}

const LEGENDA: Array<{ cor: string; rotulo: string }> = [
  { cor: '#FFF5E6', rotulo: '<2%' },
  { cor: '#FFD694', rotulo: '2–5%' },
  { cor: '#FFAE3D', rotulo: '5–12%' },
  { cor: '#F58406', rotulo: '>12%' },
];

export function MapaBairros({
  bairros,
  aoClicar,
  unidade = 'Bairro',
}: {
  bairros: BairroVoto[];
  aoClicar: (b: BairroVoto) => void;
  /** Nome da unidade territorial do mandato (ex.: "Bairro", "Distrito"). */
  unidade?: string;
}) {
  const unidadeLower = unidade.toLowerCase();
  const comDesenho = bairros.filter((b) => b.path);

  return (
    <div>
      {comDesenho.length === 0 ? (
        <div style={{ padding: '24px 8px', color: 'var(--ink3)', fontSize: 13, lineHeight: 1.5 }}>
          Nenhum território tem desenho de mapa cadastrado (`mapa_path`). O mapa coroplético
          depende desse desenho por bairro.
        </div>
      ) : (
        <div className="map">
          <svg viewBox="0 0 640 360" role="img" aria-label="Mapa de votação por bairro">
            <g stroke="var(--surface)" strokeWidth={2}>
              {comDesenho.map((b) => (
                <path
                  key={b.id}
                  d={b.path}
                  fill={cor(b.percentual)}
                  style={{ cursor: 'pointer' }}
                  onClick={() => aoClicar(b)}
                >
                  <title>
                    {b.nome} · {b.percentual}% dos votos · {b.votos.toLocaleString('pt-BR')} votos
                  </title>
                </path>
              ))}
            </g>
            <g fontFamily="Manrope" fontSize={11} fontWeight={700} pointerEvents="none">
              {comDesenho.map((b) => (
                <text key={b.id} x={b.lx} y={b.ly} fill={b.percentual > 5 ? '#fff' : '#3a2f5c'}>
                  {b.nome} {b.percentual}%
                </text>
              ))}
            </g>
          </svg>
        </div>
      )}

      <div className="legend" style={{ marginTop: 10 }}>
        {LEGENDA.map((l) => (
          <span key={l.rotulo}>
            <i style={{ background: l.cor }} /> {l.rotulo}
          </span>
        ))}
        <span style={{ marginLeft: 'auto' }}>
          clique em um {unidadeLower} · passe o mouse para detalhes
        </span>
      </div>
      <p style={{ fontSize: 11, color: 'var(--ink3)', margin: '6px 0 0' }}>
        Retrato mais recente do desempenho eleitoral por {unidadeLower}. Não muda ao trocar a
        eleição selecionada acima.
      </p>
    </div>
  );
}
